import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { useAiSettingsStore } from '../../../src/stores/aiSettingsStore';
import { settingsApi } from '../../../src/api/client';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { getDescriptor } from '../../../src/services/llm.promptRegistry';
import type { PromptKey } from '../../../src/services/llm.promptRegistry';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { padding: 16, paddingBottom: 40, gap: 20 },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...theme.shadows.sm,
  },
  cardContent: { padding: 16 },
  promptLabel: { fontSize: theme.fs(16), fontWeight: '600' as const, color: theme.colors.textPrimary, marginBottom: 4 },
  promptDescription: { fontSize: theme.fs(13), color: theme.colors.textMuted, marginBottom: 12, lineHeight: theme.fs(18) },
  textInput: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: theme.fs(13),
    fontFamily: 'monospace',
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.bgTertiary,
    minHeight: 150,
    textAlignVertical: 'top' as const,
    lineHeight: theme.fs(19),
  },
  placeholderSection: {
    marginTop: 10,
    padding: 10,
    borderRadius: 6,
    backgroundColor: theme.colors.bgTertiary,
  },
  placeholderTitle: { fontSize: theme.fs(12), fontWeight: '600' as const, color: theme.colors.textMuted, marginBottom: 6 },
  placeholderRow: { flexDirection: 'row' as const, marginBottom: 3 },
  placeholderKey: { fontSize: theme.fs(12), fontFamily: 'monospace', color: theme.colors.primary, fontWeight: '600' as const },
  placeholderDesc: { fontSize: theme.fs(12), color: theme.colors.textMuted, marginLeft: 4 },
  buttonRow: {
    flexDirection: 'row' as const,
    gap: 10,
    marginTop: 14,
  },
  saveBtn: {
    flex: 1,
    backgroundColor: theme.colors.primary,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center' as const,
  },
  saveBtnDisabled: { opacity: 0.5 },
  saveBtnText: { fontSize: theme.fs(14), fontWeight: '600' as const, color: theme.colors.onPrimary },
  resetBtn: {
    flex: 1,
    backgroundColor: theme.colors.bgTertiary,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center' as const,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  resetBtnText: { fontSize: theme.fs(14), fontWeight: '600' as const, color: theme.colors.error },
  statusBadge: {
    fontSize: theme.fs(11),
    fontWeight: '600' as const,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: 'hidden' as const,
    alignSelf: 'flex-start' as const,
    marginBottom: 8,
  },
  defaultBadge: {
    backgroundColor: theme.colors.bgTertiary,
    color: theme.colors.textMuted,
  },
  customBadge: {
    backgroundColor: theme.colors.primaryBg,
    color: theme.colors.primary,
  },
});

interface PromptEditorState {
  text: string;
  isDirty: boolean;
  isSaving: boolean;
}

export default function PromptEditorScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ category: string; keys: string }>();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const category = params.category ?? 'Prompts';
  const keys: PromptKey[] = useMemo(() => {
    try {
      return JSON.parse(params.keys ?? '[]');
    } catch {
      return [];
    }
  }, [params.keys]);

  const customPrompts = useAiSettingsStore((s) => s.customPrompts);
  const setCustomPromptStore = useAiSettingsStore((s) => s.setCustomPrompt);

  // Track editing state for each prompt key
  const [editorStates, setEditorStates] = useState<Record<string, PromptEditorState>>(() => {
    const initial: Record<string, PromptEditorState> = {};
    for (const key of keys) {
      const descriptor = getDescriptor(key);
      const customValue = customPrompts?.[key];
      initial[key] = {
        text: customValue ?? descriptor?.getDefault() ?? '',
        isDirty: false,
        isSaving: false,
      };
    }
    return initial;
  });

  const updateEditorText = (key: string, text: string) => {
    const descriptor = getDescriptor(key as PromptKey);
    const currentSaved = customPrompts?.[key] ?? descriptor?.getDefault() ?? '';
    setEditorStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], text, isDirty: text !== currentSaved },
    }));
  };

  const handleSave = async (key: PromptKey) => {
    const state = editorStates[key];
    if (!state || !state.isDirty) return;
    if (!requireConnectivity()) return;

    setEditorStates((prev) => ({ ...prev, [key]: { ...prev[key], isSaving: true } }));
    try {
      const response = await settingsApi.update({
        custom_prompts: { [key]: state.text },
      });
      useAiSettingsStore.getState().loadFromSettings(response.data);
      setEditorStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], isDirty: false, isSaving: false },
      }));
    } catch {
      setEditorStates((prev) => ({ ...prev, [key]: { ...prev[key], isSaving: false } }));
      showAlert('Error', 'Failed to save custom prompt.');
    }
  };

  const handleReset = (key: PromptKey) => {
    if (!requireConnectivity()) return;
    showConfirm(
      'Reset to Default',
      'Your custom prompt will be removed and the default will be restored.',
      async () => {
        const descriptor = getDescriptor(key);
        const defaultText = descriptor?.getDefault() ?? '';

        setEditorStates((prev) => ({
          ...prev,
          [key]: { text: defaultText, isDirty: false, isSaving: true },
        }));

        try {
          const currentCustom = { ...(customPrompts ?? {}) };
          delete currentCustom[key];
          const response = await settingsApi.update({
            custom_prompts: Object.keys(currentCustom).length > 0 ? currentCustom : null,
          });
          useAiSettingsStore.getState().loadFromSettings(response.data);
          setEditorStates((prev) => ({ ...prev, [key]: { ...prev[key], isSaving: false } }));
        } catch {
          setEditorStates((prev) => ({ ...prev, [key]: { ...prev[key], isSaving: false } }));
          showAlert('Error', 'Failed to reset prompt.');
        }
      },
      { confirmText: 'Reset', destructive: true },
    );
  };

  return (
    <View style={styles.container}>
      <ScreenHeader
        title={category}
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'prompt-editor.back-button',
        }}
      />
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        {keys.map((key) => {
          const descriptor = getDescriptor(key);
          if (!descriptor) return null;

          const state = editorStates[key];
          const isCustom = customPrompts?.[key] != null;

          return (
            <View key={key} style={styles.card} accessible={true} accessibilityLabel={`${descriptor.label} prompt, ${isCustom ? 'custom' : 'default'}`}>
              <View style={styles.cardContent}>
                {/* Status badge */}
                <Text style={[styles.statusBadge, isCustom ? styles.customBadge : styles.defaultBadge]} accessibilityLabel={isCustom ? 'Custom prompt' : 'Default prompt'}>
                  {isCustom ? 'Custom' : 'Default'}
                </Text>

                <Text style={styles.promptLabel} accessibilityRole="header">{descriptor.label}</Text>
                <Text style={styles.promptDescription}>{descriptor.description}</Text>

                {/* Prompt text editor */}
                <TextInput
                  style={styles.textInput}
                  value={state?.text ?? ''}
                  onChangeText={(text) => updateEditorText(key, text)}
                  multiline
                  scrollEnabled
                  autoCapitalize="none"
                  autoCorrect={false}
                  accessibilityLabel={`${descriptor.label} prompt text`}
                  testID={`prompt-editor.input-${key}`}
                />

                {/* Placeholder legend */}
                <View style={styles.placeholderSection} accessibilityLabel="Available placeholders">
                  <Text style={styles.placeholderTitle} accessible={false}>Available Placeholders</Text>
                  {descriptor.placeholders.map((ph) => (
                    <View key={ph} style={styles.placeholderRow} accessible={true} accessibilityLabel={`Placeholder ${ph}: ${descriptor.placeholderDescriptions[ph] ?? ''}`}>
                      <Text style={styles.placeholderKey} accessible={false}>{`{{${ph}}}`}</Text>
                      <Text style={styles.placeholderDesc} accessible={false}>
                        {descriptor.placeholderDescriptions[ph] ?? ''}
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Action buttons */}
                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={[styles.saveBtn, (!state?.isDirty || state?.isSaving) && styles.saveBtnDisabled]}
                    disabled={!state?.isDirty || state?.isSaving}
                    onPress={() => handleSave(key)}
                    accessibilityLabel="Save custom prompt"
                    accessibilityRole="button"
                    testID={`prompt-editor.save-${key}`}
                  >
                    {state?.isSaving ? (
                      <ActivityIndicator size="small" color={colors.onPrimary} />
                    ) : (
                      <Text style={styles.saveBtnText}>Save</Text>
                    )}
                  </TouchableOpacity>
                  {isCustom && (
                    <TouchableOpacity
                      style={styles.resetBtn}
                      onPress={() => handleReset(key)}
                      disabled={state?.isSaving}
                      accessibilityLabel="Reset to default prompt"
                      accessibilityRole="button"
                      testID={`prompt-editor.reset-${key}`}
                    >
                      <Text style={styles.resetBtnText}>Reset to Default</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}
